import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { 
  Calendar, 
  Mail, 
  Phone, 
  User, 
  Clock, 
  CheckCircle,
  XCircle,
  Trash2,
  Eye,
  X,
  Plus,
  Lock
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { format } from "date-fns";
import { nl } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const [activeTab, setActiveTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Availability management
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [blockReason, setBlockReason] = useState("");
  const [blockWholeDay, setBlockWholeDay] = useState(false);

  const timeSlots = [
    "10:00", "10:30", "11:00", "11:30", "12:00", "12:30",
    "15:00", "15:30", "16:00", "16:30", "17:00", "17:30",
    "18:00", "18:30", "19:00", "19:30", "20:00", "20:30", "21:00", "21:30"
  ];

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bookingsRes, contactsRes, blockedRes] = await Promise.all([
        axios.get(`${API}/bookings`),
        axios.get(`${API}/contact`),
        axios.get(`${API}/blocked-slots`),
      ]);
      setBookings(bookingsRes.data);
      setContacts(contactsRes.data);
      setBlockedSlots(blockedRes.data);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error("Error fetching data:", err);
      toast.error("Fout bij het laden van data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Memoize sorted blocked slots
  const sortedBlockedSlots = useMemo(() => 
    [...blockedSlots].sort((a, b) => new Date(a.datum) - new Date(b.datum)),
    [blockedSlots]
  );

  const handleBlockSlot = async () => {
    if (!selectedDate) {
      toast.error("Selecteer eerst een datum");
      return;
    }
    if (!blockWholeDay && !selectedTime) {
      toast.error("Selecteer een tijdslot of blokkeer de hele dag");
      return;
    }

    try {
      await axios.post(`${API}/blocked-slots`, {
        datum: format(selectedDate, "yyyy-MM-dd"),
        tijd: blockWholeDay ? null : selectedTime,
        reden: blockReason || (blockWholeDay ? "Hele dag gesloten" : "Niet beschikbaar"),
      });
      toast.success(blockWholeDay ? "Dag geblokkeerd" : "Tijdslot geblokkeerd");
      setSelectedDate(null);
      setSelectedTime("");
      setBlockReason("");
      setBlockWholeDay(false);
      fetchData();
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error("Error blocking slot:", err);
      toast.error("Fout bij blokkeren");
    }
  };

  const handleUnblockSlot = async (slotId) => {
    try {
      await axios.delete(`${API}/blocked-slots/${slotId}`);
      toast.success("Slot vrijgegeven");
      fetchData();
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error("Error unblocking slot:", err);
      toast.error("Fout bij vrijgeven");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("nl-BE", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString("nl-BE", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "bg-green-500/20 text-green-400";
      case "cancelled":
        return "bg-red-500/20 text-red-400";
      default:
        return "bg-yellow-500/20 text-yellow-400";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "confirmed":
        return "Bevestigd";
      case "cancelled":
        return "Geannuleerd";
      default:
        return "In afwachting";
    }
  };

  return (
    <main data-testid="admin-page" className="pt-20 min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <section className="py-12 bg-[#0A0A0A] border-b border-zinc-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-4xl font-black text-white font-['Barlow_Condensed'] uppercase tracking-tighter">
                Admin <span className="text-red-500">Dashboard</span>
              </h1>
              <p className="text-zinc-400 mt-2">
                Beheer boekingen, berichten en beschikbaarheid
              </p>
            </div>
            <button
              onClick={fetchData}
              className="btn-secondary text-sm"
              data-testid="refresh-btn"
            >
              Vernieuwen
            </button>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap gap-4 mb-8">
          <button
            onClick={() => setActiveTab("bookings")}
            className={`px-6 py-3 rounded-sm font-bold uppercase tracking-wide transition-all ${
              activeTab === "bookings"
                ? "bg-red-500 text-white"
                : "bg-zinc-900 text-zinc-400 hover:text-white"
            }`}
            data-testid="tab-bookings"
          >
            <Calendar className="inline-block w-5 h-5 mr-2" />
            Boekingen ({bookings.length})
          </button>
          <button
            onClick={() => setActiveTab("contacts")}
            className={`px-6 py-3 rounded-sm font-bold uppercase tracking-wide transition-all ${
              activeTab === "contacts"
                ? "bg-red-500 text-white"
                : "bg-zinc-900 text-zinc-400 hover:text-white"
            }`}
            data-testid="tab-contacts"
          >
            <Mail className="inline-block w-5 h-5 mr-2" />
            Berichten ({contacts.length})
          </button>
          <button
            onClick={() => setActiveTab("availability")}
            className={`px-6 py-3 rounded-sm font-bold uppercase tracking-wide transition-all ${
              activeTab === "availability"
                ? "bg-red-500 text-white"
                : "bg-zinc-900 text-zinc-400 hover:text-white"
            }`}
            data-testid="tab-availability"
          >
            <Lock className="inline-block w-5 h-5 mr-2" />
            Beschikbaarheid ({blockedSlots.length})
          </button>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-zinc-400">Laden...</p>
          </div>
        ) : (
          <>
            {/* Bookings Tab */}
            {activeTab === "bookings" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="bookings-list"
              >
                {bookings.length === 0 ? (
                  <div className="text-center py-20 bg-zinc-900/50 rounded-sm border border-zinc-800">
                    <Calendar className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                    <p className="text-zinc-400">Nog geen boekingen</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-zinc-800">
                          <th className="text-left py-4 px-4 text-zinc-400 font-bold uppercase text-sm">
                            Naam
                          </th>
                          <th className="text-left py-4 px-4 text-zinc-400 font-bold uppercase text-sm">
                            Dienst
                          </th>
                          <th className="text-left py-4 px-4 text-zinc-400 font-bold uppercase text-sm">
                            Datum & Tijd
                          </th>
                          <th className="text-left py-4 px-4 text-zinc-400 font-bold uppercase text-sm">
                            Contact
                          </th>
                          <th className="text-left py-4 px-4 text-zinc-400 font-bold uppercase text-sm">
                            Status
                          </th>
                          <th className="text-left py-4 px-4 text-zinc-400 font-bold uppercase text-sm">
                            Acties
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {bookings.map((booking) => (
                          <tr
                            key={booking.id}
                            className="border-b border-zinc-800/50 hover:bg-zinc-900/50 transition-colors"
                            data-testid={`booking-row-${booking.id}`}
                          >
                            <td className="py-4 px-4">
                              <p className="text-white font-bold">{booking.naam}</p>
                            </td>
                            <td className="py-4 px-4">
                              <p className="text-zinc-300">{booking.service_type}</p>
                            </td>
                            <td className="py-4 px-4">
                              <p className="text-white">{formatDate(booking.datum)}</p>
                              <p className="text-zinc-500 text-sm">{booking.tijd}</p>
                            </td>
                            <td className="py-4 px-4">
                              <p className="text-zinc-300 text-sm">{booking.email}</p>
                              <p className="text-zinc-500 text-sm">{booking.telefoon}</p>
                            </td>
                            <td className="py-4 px-4">
                              <span
                                className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${getStatusColor(
                                  booking.status
                                )}`}
                              >
                                {getStatusText(booking.status)}
                              </span>
                            </td>
                            <td className="py-4 px-4">
                              <button
                                onClick={() => setSelectedItem(booking)}
                                className="text-zinc-400 hover:text-white transition-colors p-2"
                                title="Bekijken"
                              >
                                <Eye size={18} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </motion.div>
            )}

            {/* Contacts Tab */}
            {activeTab === "contacts" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="contacts-list"
              >
                {contacts.length === 0 ? (
                  <div className="text-center py-20 bg-zinc-900/50 rounded-sm border border-zinc-800">
                    <Mail className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                    <p className="text-zinc-400">Nog geen berichten</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {contacts.map((contact) => (
                      <div
                        key={contact.id}
                        className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 hover:border-red-500/30 transition-all cursor-pointer"
                        onClick={() => setSelectedItem(contact)}
                        data-testid={`contact-card-${contact.id}`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-white font-bold">{contact.naam}</h3>
                            <p className="text-zinc-500 text-sm">
                              {formatDateTime(contact.created_at)}
                            </p>
                          </div>
                          <Mail className="w-5 h-5 text-red-500" />
                        </div>
                        <p className="text-zinc-400 text-sm mb-4 line-clamp-2">
                          {contact.bericht || "Geen bericht"}
                        </p>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-zinc-500">{contact.email}</span>
                        </div>
                        {contact.doelstelling && (
                          <span className="inline-block mt-3 px-3 py-1 bg-red-500/20 text-red-400 text-xs rounded-full">
                            {contact.doelstelling}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Availability Tab */}
            {activeTab === "availability" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="availability-section"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Block Slot Form */}
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
                    <h2 className="text-xl font-black text-white mb-6 font-['Barlow_Condensed'] uppercase">
                      <Lock className="inline-block w-5 h-5 mr-2 text-red-500" />
                      Slot Blokkeren
                    </h2>

                    <div className="mb-6">
                      <label className="block text-sm text-zinc-400 mb-2">
                        Selecteer datum
                      </label>
                      <div className="bg-zinc-800/50 rounded-sm p-4 flex justify-center">
                        <CalendarPicker
                          mode="single"
                          selected={selectedDate}
                          onSelect={setSelectedDate}
                          locale={nl}
                          className="text-white"
                          data-testid="block-calendar"
                        />
                      </div>
                    </div>

                    <div className="mb-6">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={blockWholeDay}
                          onChange={(e) => setBlockWholeDay(e.target.checked)}
                          className="w-5 h-5 rounded border-zinc-600 bg-zinc-800 text-red-500 focus:ring-red-500"
                        />
                        <span className="text-white font-bold">Hele dag blokkeren</span>
                      </label>
                    </div>

                    {!blockWholeDay && (
                      <div className="mb-6">
                        <label className="block text-sm text-zinc-400 mb-2">
                          Selecteer tijdslot
                        </label>
                        <div className="grid grid-cols-4 gap-2 max-h-[200px] overflow-y-auto">
                          {timeSlots.map((slot) => (
                            <button
                              key={slot}
                              onClick={() => setSelectedTime(slot)}
                              className={`py-2 px-3 rounded-sm text-sm font-bold transition-all ${
                                selectedTime === slot
                                  ? "bg-red-500 text-white"
                                  : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
                              }`}
                            >
                              {slot}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mb-6">
                      <label className="block text-sm text-zinc-400 mb-2">
                        Reden (optioneel)
                      </label>
                      <input
                        type="text"
                        value={blockReason}
                        onChange={(e) => setBlockReason(e.target.value)}
                        className="form-input"
                        placeholder="Bijv. Vakantie, Vol, Afwezig..."
                      />
                    </div>

                    <button
                      onClick={handleBlockSlot}
                      className="btn-primary w-full flex items-center justify-center gap-2"
                      data-testid="block-slot-btn"
                    >
                      <Plus size={18} />
                      {blockWholeDay ? "Dag Blokkeren" : "Slot Blokkeren"}
                    </button>
                  </div>

                  {/* Blocked Slots List */}
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
                    <h2 className="text-xl font-black text-white mb-6 font-['Barlow_Condensed'] uppercase">
                      Geblokkeerde Slots
                    </h2>

                    {blockedSlots.length === 0 ? (
                      <div className="text-center py-12">
                        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                        <p className="text-zinc-400">Alles is beschikbaar</p>
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-[500px] overflow-y-auto">
                        {sortedBlockedSlots.map((slot) => (
                          <div
                            key={slot.id}
                            className="flex items-center justify-between bg-zinc-800/50 p-4 rounded-sm"
                            data-testid={`blocked-slot-${slot.id}`}
                          >
                            <div>
                              <p className="text-white font-bold">
                                {formatDate(slot.datum)}
                                {slot.tijd && (
                                  <span className="text-red-500 ml-2">
                                    {slot.tijd}
                                  </span>
                                )}
                              </p>
                              <p className="text-zinc-500 text-sm">
                                {slot.tijd ? "Tijdslot geblokkeerd" : "Hele dag gesloten"}
                                {slot.reden && ` - ${slot.reden}`}
                              </p>
                            </div>
                            <button
                              onClick={() => handleUnblockSlot(slot.id)}
                              className="text-zinc-400 hover:text-red-500 transition-colors p-2"
                              title="Vrijgeven"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </>
        )}
      </div>

      {/* Detail Modal */}
      {selectedItem && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setSelectedItem(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-zinc-900 border border-zinc-800 rounded-sm max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="detail-modal"
          >
            <div className="flex items-center justify-between p-6 border-b border-zinc-800">
              <h2 className="text-xl font-black text-white font-['Barlow_Condensed'] uppercase">
                {selectedItem.service_type ? "Boeking Details" : "Bericht Details"}
              </h2>
              <button
                onClick={() => setSelectedItem(null)}
                className="text-zinc-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-red-500" />
                <div>
                  <p className="text-zinc-500 text-sm">Naam</p>
                  <p className="text-white font-bold">{selectedItem.naam}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-red-500" />
                <div>
                  <p className="text-zinc-500 text-sm">E-mail</p>
                  <a
                    href={`mailto:${selectedItem.email}`}
                    className="text-white hover:text-red-500 transition-colors"
                  >
                    {selectedItem.email}
                  </a>
                </div>
              </div>
              {selectedItem.telefoon && (
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-red-500" />
                  <div>
                    <p className="text-zinc-500 text-sm">Telefoon</p>
                    <a
                      href={`tel:${selectedItem.telefoon}`}
                      className="text-white hover:text-red-500 transition-colors"
                    >
                      {selectedItem.telefoon}
                    </a>
                  </div>
                </div>
              )}
              {selectedItem.service_type && (
                <>
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-red-500" />
                    <div>
                      <p className="text-zinc-500 text-sm">Dienst</p>
                      <p className="text-white">{selectedItem.service_type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-red-500" />
                    <div>
                      <p className="text-zinc-500 text-sm">Datum & Tijd</p>
                      <p className="text-white">
                        {formatDate(selectedItem.datum)} om {selectedItem.tijd}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm mb-2">Status</p>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${getStatusColor(
                        selectedItem.status
                      )}`}
                    >
                      {getStatusText(selectedItem.status)}
                    </span>
                  </div>
                </>
              )}
              {selectedItem.doelstelling && (
                <div>
                  <p className="text-zinc-500 text-sm">Doelstelling</p>
                  <p className="text-white">{selectedItem.doelstelling}</p>
                </div>
              )}
              {(selectedItem.bericht || selectedItem.notities) && (
                <div>
                  <p className="text-zinc-500 text-sm mb-2">
                    {selectedItem.bericht ? "Bericht" : "Notities"}
                  </p>
                  <p className="text-zinc-300 bg-zinc-800/50 p-4 rounded-sm">
                    {selectedItem.bericht || selectedItem.notities}
                  </p>
                </div>
              )}
              <div className="pt-4 border-t border-zinc-800">
                <p className="text-zinc-500 text-sm">
                  Ontvangen: {formatDateTime(selectedItem.created_at)}
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-zinc-800 flex gap-4">
              <a
                href={`mailto:${selectedItem.email}`}
                className="btn-primary flex-1 text-center"
              >
                E-mail Sturen
              </a>
              {selectedItem.telefoon && (
                <a
                  href={`tel:${selectedItem.telefoon}`}
                  className="btn-secondary flex-1 text-center"
                >
                  Bellen
                </a>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </main>
  );
};

export default AdminPage;
